import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { API_BASE } from "../config/api";
import { formatAuthErrorMessage } from "./authErrorUtils";
import {
  saveAuthTokens,
  clearAuthTokens,
  getAuthToken,
  getRefreshToken,
  refreshAccessToken
} from "../services/api";

export type AuthScreen = "login" | "register" | "forgot" | "reset-2fa";

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  businessName?: string;
  phone?: string;
  avatarUrl?: string;
  plan: "starter" | "business" | "pro" | "agency" | "scale" | "enterprise" | "free" | string;
  twoFactorEnabled?: boolean;
  emailVerified?: boolean;
  isPartner?: boolean;
  partnerCode?: string;
  partnerLink?: string;
  partnerStatus?: string;
  partnerApprovedAt?: string;
  partnerProExpiresAt?: string;
  viralLoopEnabled?: boolean;
  viralLoopActivatedAt?: string;
  viralLoopLockedUntil?: string;
  viralLoopBonusMessages?: number;
  createdAt: string;
}

export interface LoginResult {
  requires2FA?: boolean;
  tempToken?: string;
}

export interface UpdateProfileResult {
  user?: AuthUser;
  emailVerificationRequired?: boolean;
  pendingEmail?: string;
  message?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isVerifying: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginWithGoogle: (payload: { credential?: string; accessToken?: string; code?: string }) => Promise<LoginResult>;
  verify2FA: (tempToken: string, code: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<{ requiresVerification?: boolean; email?: string } | void>;
  verifyRegistration: (email: string, otp: string) => Promise<void>;
  resendRegistrationOtp: (email: string) => Promise<void>;
  updateProfile: (data: { fullName?: string; phone?: string; businessName?: string; email?: string }) => Promise<UpdateProfileResult>;
  verifyEmailChange: (otp: string, pendingEmail?: string) => Promise<{ success: boolean; message: string; user?: AuthUser }>;
  resendEmailChangeOtp: () => Promise<{ success: boolean; message: string }>;
  cancelEmailChange: () => Promise<{ success: boolean; message: string }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  toggleViralLoop: (enabled: boolean) => Promise<any>;
  refreshUser: () => Promise<void>;
  updateUserDirectly: (partial: Partial<AuthUser>) => void;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isVerifying: false,
      login: async () => ({ requires2FA: false }),
      loginWithGoogle: async () => ({ requires2FA: false }),
      verify2FA: async () => {},
      register: async () => {},
      verifyRegistration: async () => {},
      resendRegistrationOtp: async () => {},
      updateProfile: async () => ({} as any),
      verifyEmailChange: async () => ({ success: false, message: '' }),
      resendEmailChangeOtp: async () => ({ success: false, message: '' }),
      cancelEmailChange: async () => ({ success: false, message: '' }),
      updatePassword: async () => {},
      toggleViralLoop: async () => ({ success: false, viralLoopEnabled: false }),
      refreshUser: async () => {},
      updateUserDirectly: () => {},
      logout: async () => {},
      error: null,
      clearError: () => {}
    };
  }
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem('zeyilo_auth_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(() => {
    try {
      return Boolean(localStorage.getItem('zeyilo_auth_user') || getAuthToken());
    } catch {
      return false;
    }
  });

  const clearError = useCallback(() => setError(null), []);

  // Synchronisation cross-onglets via l'événement storage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'zeyilo_auth_user') {
        try {
          const storedUser = e.newValue ? JSON.parse(e.newValue) : null;
          setUser(storedUser);
        } catch {
          setUser(null);
        }
      } else if (e.key === 'zeyilo_auth_token' && !e.newValue) {
        setUser(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Écoute de l'événement global de déconnexion si la session expire définitivement
  useEffect(() => {
    const handleExpired = () => {
      setUser(null);
      clearAuthTokens();
      try {
        localStorage.removeItem('zeyilo_auth_user');
      } catch {}
    };
    window.addEventListener('zeyilo_session_expired', handleExpired);
    return () => window.removeEventListener('zeyilo_session_expired', handleExpired);
  }, []);

  // Proactive Silent Token Refresh : rafraîchit le token toutes les 10 minutes en tâche de fond tant que connecté
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      try {
        await refreshAccessToken();
      } catch {}
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // Vérification de la session au montage — hybride Bearer + cookies
  useEffect(() => {
    let hasStoredUser = false;
    try {
      hasStoredUser = Boolean(localStorage.getItem('zeyilo_auth_user') || getAuthToken());
    } catch {}

    if (!hasStoredUser) {
      setIsVerifying(false);
      return;
    }

    const checkMe = async () => {
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const res = await fetch(`${API_BASE}/auth/me`, {
          credentials: 'include',
          headers
        });

        if (res.ok) {
          const data = await res.json();
          if (data.success && data.user) {
            setUser(data.user);
            localStorage.setItem('zeyilo_auth_user', JSON.stringify(data.user));
            return;
          }
        }

        // Access token expiré/absent : tenter le refresh transparent via refreshAccessToken
        if (res.status === 401 || res.status === 403) {
          const newToken = await refreshAccessToken();
          if (newToken) {
            const retryRes = await fetch(`${API_BASE}/auth/me`, {
              credentials: 'include',
              headers: {
                Authorization: `Bearer ${newToken}`
              }
            });
            if (retryRes.ok) {
              const retryData = await retryRes.json();
              if (retryData.success && retryData.user) {
                setUser(retryData.user);
                localStorage.setItem('zeyilo_auth_user', JSON.stringify(retryData.user));
                return;
              }
            }
          }
        }

        // Session définitivement absente ou révoquée : vider l'état local
        if (res.status === 401 || res.status === 403) {
          setUser(null);
          clearAuthTokens();
          localStorage.removeItem('zeyilo_auth_user');
        }
      } catch {
        // En cas de micro-coupure réseau, préserver l'état local
      } finally {
        setIsVerifying(false);
      }
    };

    checkMe();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    setIsLoading(true);
    setError(null);
    try {
      if (!email || !password) throw new Error("Email et mot de passe requis.");

      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const text = await res.text();
      const data: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || `Erreur ${res.status} : Connexion échouée`);
      }

      if (data.requires2FA) {
        return {
          requires2FA: true,
          tempToken: data.tempToken
        };
      }

      setUser(data.user);
      localStorage.setItem('zeyilo_auth_user', JSON.stringify(data.user));
      try { localStorage.removeItem('zeyilo_selected_account_id'); } catch {}
      saveAuthTokens(data.token, data.refreshToken);

      return { requires2FA: false };
    } catch (e: unknown) {
      const friendlyMsg = formatAuthErrorMessage(e, "Erreur de connexion.");
      setError(friendlyMsg);
      throw new Error(friendlyMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(async (payload: { credential?: string; accessToken?: string; code?: string }): Promise<LoginResult> => {
    setIsLoading(true);
    setError(null);
    try {
      if (!payload.credential && !payload.code && !payload.accessToken) {
        throw new Error("Jeton Google requis pour la connexion.");
      }

      let referredBy: string | undefined = undefined;
      try {
        referredBy = localStorage.getItem('zeyilo_partner_ref') || undefined;
      } catch {}

      const res = await fetch(`${API_BASE}/auth/google`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, referredBy })
      });

      const text = await res.text();
      const data: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || `Erreur ${res.status} : Échec de la connexion avec Google`);
      }

      if (data.requires2FA) {
        return {
          requires2FA: true,
          tempToken: data.tempToken
        };
      }

      setUser(data.user);
      localStorage.setItem('zeyilo_auth_user', JSON.stringify(data.user));
      try { localStorage.removeItem('zeyilo_selected_account_id'); } catch {}
      saveAuthTokens(data.token, data.refreshToken);

      return { requires2FA: false };
    } catch (e: unknown) {
      const friendlyMsg = formatAuthErrorMessage(e, "Erreur de connexion avec Google.");
      setError(friendlyMsg);
      throw new Error(friendlyMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verify2FA = useCallback(async (tempToken: string, code: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      if (!tempToken || !code) throw new Error("Code de vérification requis.");

      const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, code })
      });

      const text = await res.text();
      const data: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || `Erreur ${res.status} : Code invalide`);
      }

      setUser(data.user);
      localStorage.setItem('zeyilo_auth_user', JSON.stringify(data.user));
      saveAuthTokens(data.token, data.refreshToken);
    } catch (e: unknown) {
      const friendlyMsg = formatAuthErrorMessage(e, "Code de vérification 2FA invalide.");
      setError(friendlyMsg);
      throw new Error(friendlyMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (email: string, password: string, fullName: string) => {
    setIsLoading(true);
    setError(null);
    try {
      if (!email || !password || !fullName) throw new Error("Tous les champs obligatoires doivent être remplis.");

      let referredBy: string | undefined = undefined;
      try {
        referredBy = localStorage.getItem('zeyilo_partner_ref') || undefined;
      } catch {}

      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, fullName, referredBy })
      });

      const text = await res.text();
      const data: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || `Erreur ${res.status} : Inscription échouée`);
      }

      if (data.requiresVerification) {
        return { requiresVerification: true, email: data.email || email };
      }

      setUser(data.user);
      localStorage.setItem('zeyilo_auth_user', JSON.stringify(data.user));
      saveAuthTokens(data.token, data.refreshToken);
      return { requiresVerification: false };
    } catch (e: unknown) {
      const friendlyMsg = formatAuthErrorMessage(e, "Erreur lors de l'inscription.");
      setError(friendlyMsg);
      throw new Error(friendlyMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyRegistration = useCallback(async (email: string, otp: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      if (!email || !otp) throw new Error("Email et code de vérification requis.");

      const res = await fetch(`${API_BASE}/auth/register/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), otp: otp.trim() })
      });

      const text = await res.text();
      const data: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || "Code de vérification invalide ou expiré");
      }

      setUser(data.user);
      localStorage.setItem('zeyilo_auth_user', JSON.stringify(data.user));
      saveAuthTokens(data.token, data.refreshToken);
    } catch (e: unknown) {
      const friendlyMsg = formatAuthErrorMessage(e, "Erreur lors de la vérification du code.");
      setError(friendlyMsg);
      throw new Error(friendlyMsg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resendRegistrationOtp = useCallback(async (email: string): Promise<void> => {
    try {
      if (!email) throw new Error("Adresse e-mail requise.");
      const res = await fetch(`${API_BASE}/auth/register/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Impossible d'envoyer un nouveau code.");
      }
    } catch (e: any) {
      const friendlyMsg = formatAuthErrorMessage(e, "Impossible de renvoyer le code.");
      throw new Error(friendlyMsg);
    }
  }, []);

  const updateProfile = useCallback(async (data: { fullName?: string; phone?: string; businessName?: string; email?: string }): Promise<UpdateProfileResult> => {
    const res = await fetch(`${API_BASE}/auth/profile`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    const text = await res.text();
    const result: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
    if (!res.ok || !result.success) {
      throw new Error(result.error || result.message || 'Échec de la mise à jour du profil');
    }

    const updatedUser = result.user;
    if (updatedUser) {
      setUser(updatedUser);
      localStorage.setItem('zeyilo_auth_user', JSON.stringify(updatedUser));
    }
    return {
      user: updatedUser,
      emailVerificationRequired: Boolean(result.emailVerificationRequired),
      pendingEmail: result.pendingEmail,
      message: result.message
    };
  }, []);

  const verifyEmailChange = useCallback(async (otp: string, pendingEmail?: string): Promise<{ success: boolean; message: string; user?: AuthUser }> => {
    const res = await fetch(`${API_BASE}/auth/profile/verify-email-change`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ otp, pendingEmail })
    });

    const text = await res.text();
    const result: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
    if (!res.ok || !result.success) {
      throw new Error(result.error || result.message || 'Code de vérification invalide');
    }

    const updatedUser = result.user;
    if (updatedUser) {
      setUser(updatedUser);
      localStorage.setItem('zeyilo_auth_user', JSON.stringify(updatedUser));
    }
    return {
      success: true,
      message: result.message || 'Adresse e-mail mise à jour avec succès',
      user: updatedUser
    };
  }, []);

  const resendEmailChangeOtp = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    const res = await fetch(`${API_BASE}/auth/profile/resend-email-change-otp`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });

    const text = await res.text();
    const result: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
    if (!res.ok || !result.success) {
      throw new Error(result.error || result.message || 'Échec du renvoi du code');
    }
    return { success: true, message: result.message || 'Nouveau code envoyé' };
  }, []);

  const cancelEmailChange = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    const res = await fetch(`${API_BASE}/auth/profile/cancel-email-change`, {
      method: 'DELETE',
      credentials: 'include'
    });

    const text = await res.text();
    const result: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
    return { success: true, message: result.message || 'Modification annulée' };
  }, []);

  const updatePassword = useCallback(async (currentPassword: string, newPassword: string): Promise<void> => {
    const res = await fetch(`${API_BASE}/auth/password`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword })
    });

    const text = await res.text();
    const result: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
    if (!res.ok || !result.success) {
      throw new Error(result.error || result.message || 'Échec de la modification du mot de passe');
    }
    clearAuthTokens();
  }, []);

  const toggleViralLoop = useCallback(async (enabled: boolean): Promise<any> => {
    const res = await fetch(`${API_BASE}/auth/viral-loop`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled })
    });

    const text = await res.text();
    const result: any = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
    if (!res.ok || !result.success) {
      const err: any = new Error(result.error || result.message || 'Échec de la modification');
      err.lockedUntil = result.lockedUntil;
      err.isLocked = result.isLocked;
      throw err;
    }

    setUser(prev => {
      if (!prev) return null;
      const updated = {
        ...prev,
        viralLoopEnabled: Boolean(result.viralLoopEnabled),
        viralLoopLockedUntil: result.viralLoopLockedUntil || prev.viralLoopLockedUntil,
        viralLoopBonusMessages: result.viralLoopBonusMessages || prev.viralLoopBonusMessages || 50
      };
      try {
        localStorage.setItem('zeyilo_auth_user', JSON.stringify(updated));
      } catch {}
      return updated;
    });

    return result;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.user) {
          setUser(data.user);
          localStorage.setItem('zeyilo_auth_user', JSON.stringify(data.user));
        }
      }
    } catch {}
  }, []);

  const updateUserDirectly = useCallback((partial: Partial<AuthUser>) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...partial };
      try {
        localStorage.setItem('zeyilo_auth_user', JSON.stringify(updated));
      } catch {}
      return updated;
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch {}
    setUser(null);
    localStorage.removeItem('zeyilo_auth_user');
    try { localStorage.removeItem('zeyilo_selected_account_id'); } catch {}
    clearAuthTokens();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading: isLoading || isVerifying,
        isVerifying,
        login,
        loginWithGoogle,
        verify2FA,
        register,
        verifyRegistration,
        resendRegistrationOtp,
        updateProfile,
        verifyEmailChange,
        resendEmailChangeOtp,
        cancelEmailChange,
        updatePassword,
        toggleViralLoop,
        refreshUser,
        updateUserDirectly,
        logout,
        error,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
